import {
  Ban,
  BarChart,
  BookOpen,
  CreditCard,
  Edit3,
  FileText,
  LayoutDashboard,
  Menu,
  RotateCcw,
  ServerCog,
  Settings,
  ShoppingCart,
  Truck,
  Users,
  X,
} from "lucide-react";
import React, { useState } from "react";
import { Toaster } from "react-hot-toast";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { cn } from "../../utils/cn";
import { ErrorBoundary } from "../common/ErrorBoundary";
import { LoadingScreen } from "../common/LoadingScreen";
import InlineLogo from "../icons/InlineLogo";
import DevSidebar from "../ui/DevSidebar";

const navigation = [
  {
    group: "Principal",
    items: [{ name: "Dashboard", href: "/", icon: LayoutDashboard }],
  },
  {
    group: "Operațiuni",
    items: [
      { name: "Comenzi", href: "/orders/", icon: ShoppingCart },
      { name: "Draft-uri", href: "/drafts/", icon: Edit3 },
      { name: "Retururi", href: "/returns/", icon: RotateCcw },
      { name: "AWB-uri", href: "/shipping/", icon: Truck },
      { name: "Facturi", href: "/invoices/", icon: FileText },
    ],
  },
  {
    group: "Management",
    items: [
      { name: "Clienți", href: "/customers/", icon: Users },
      { name: "Produse", href: "/products/", icon: BookOpen },
      { name: "Plăți", href: "/payments/", icon: CreditCard },
      { name: "Blacklist", href: "/blacklist/", icon: Ban },
    ],
  },
  {
    group: "Sistem",
    items: [
      { name: "Rapoarte", href: "/reports/", icon: BarChart },
      { name: "Monitorizare", href: "/monitoring/", icon: ServerCog },
      { name: "Setări", href: "/settings/", icon: Settings },
    ],
  },
];

export function AdminLayout() {
  const { state, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Handle navigation with loading state
  const handleNavigation = (href: string) => {
    navigate(href);
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    const redirect = encodeURIComponent(
      location.pathname + location.search + location.hash
    );
    logout();
    navigate(`/login/?redirect=${redirect}`, { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header bar with animated background */}
      <div className="fixed top-0 left-0 right-0 z-50 h-16 flex items-center justify-between shadow-2xl overflow-hidden bg-white">
        {/* Animated gradient background from black to white */}

        <div className="absolute inset-0 bg-gradient-to-r from-white via-gray-500 to-black"></div>
        {/* <div className="absolute inset-0 opacity-50">
          <div className="absolute inset-0 bg-gradient-to-r from-red-500/20 via-transparent to-transparent"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(239,68,68,0.05),transparent_50%)]"></div>
        </div> */}
        {/* Content container */}
        <div className="relative flex w-full items-center justify-between">
          {/* Left section with logo */}
          <div className="flex items-center h-full">
            {/* Mobile menu button */}
            <div className="flex lg:hidden items-center pl-4">
              <button
                type="button"
                className="text-gray-200 hover:text-white transition-colors"
                onClick={() => setMobileMenuOpen(true)}
              >
                <span className="sr-only">Deschide meniu</span>
                <Menu className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>

            {/* Logo section with advanced effects */}
            <div className="h-16 flex items-center lg:pl-0 relative group">
              {/* Simple hover background */}
              <div className="absolute inset-0">
                <div className="absolute inset-0 bg-gradient-to-r from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300"></div>
              </div>

              {/* Logo container */}
              <div className="relative flex items-center pl-16">
                {/* Logo text */}
                <div className="relative">
                  <InlineLogo className="w-22 h-10" />
                </div>
              </div>
            </div>
          </div>

          {/* Right section with user info and logout */}
          <div className="flex items-center h-full pr-6">
            <div className="flex items-center gap-x-4 pl-6 h-full relative">
              {/* Separator line */}
              <div className="absolute inset-y-0 left-0 w-px bg-gradient-to-b from-transparent via-red-500/20 to-transparent"></div>

              {/* User info */}
              <span className="text-sm text-white mr-2">
                {state.user?.email}
              </span>

              {/* Logout button */}
              <button
                onClick={handleLogout}
                className="relative px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white rounded-lg text-sm font-semibold shadow-lg hover:shadow-red-500/20 transition-all duration-300 active:transform active:scale-95 border border-red-500/20 overflow-hidden group"
              >
                {/* Button shine effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-45 translate-x-[-150%] group-hover:translate-x-[150%] transition-transform duration-700"></div>
                {/* Button glow effect */}
                <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-red-500/20 blur"></div>
                <span className="relative">Ieșire din cont</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden">
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm transition-opacity"
            onClick={() => setMobileMenuOpen(false)}
          />

          {/* Slide-over panel */}
          <div className="fixed inset-y-0 left-0 z-50 w-full overflow-y-auto bg-white px-4 pb-6 sm:max-w-sm sm:px-6 sm:ring-1 sm:ring-gray-900/10">
            <div className="flex items-center justify-between pt-4">
              <div className="text-2xl font-bold text-red-600">BFE</div>
              <button
                type="button"
                className="-m-2.5 rounded-md p-2.5 text-gray-700 transition-colors hover:text-gray-900"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="sr-only">Închide meniu</span>
                <X className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <div className="mt-6 flow-root">
              <div className="-my-6 divide-y divide-gray-500/10">
                <div className="space-y-2 py-6">
                  {navigation.map((group) => (
                    <div key={group.group} className="mb-4">
                      <div className="text-xs font-semibold leading-6 text-gray-400 uppercase tracking-wider mb-2 px-2">
                        {group.group}
                      </div>
                      {group.items.map((item) => {
                        const isActive = location.pathname.startsWith(
                          item.href
                        );
                        return (
                          <button
                            key={item.name}
                            onClick={() => handleNavigation(item.href)}
                            className={cn(
                              isActive
                                ? "bg-blue-50 text-blue-600"
                                : "text-gray-700 hover:text-blue-600 hover:bg-blue-50",
                              "group flex w-full items-center gap-x-2 rounded-md p-2 text-sm leading-6 font-semibold transition-all duration-200"
                            )}
                          >
                            <item.icon
                              className={cn(
                                isActive
                                  ? "text-blue-600"
                                  : "text-gray-400 group-hover:text-blue-600",
                                "h-5 w-5 shrink-0 transition-colors duration-200"
                              )}
                              aria-hidden="true"
                            />
                            {item.name}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-40 lg:flex lg:w-64 lg:flex-col">
        <div className="flex grow flex-col gap-y-3 overflow-y-auto border-r border-gray-200/80 bg-white px-3 pb-4 pt-20 shadow-lg">
          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-4">
              {navigation.map((group) => (
                <li key={group.group}>
                  <div className="text-xs font-semibold leading-6 text-gray-400 uppercase tracking-wider mb-1 px-1">
                    {group.group}
                  </div>
                  <ul role="list" className="space-y-0.5">
                    {group.items.map((item) => {
                      const isActive = location.pathname.startsWith(item.href);
                      return (
                        <li key={item.name}>
                          <button
                            onClick={() => handleNavigation(item.href)}
                            className={cn(
                              isActive
                                ? "bg-blue-50 text-blue-600"
                                : "text-gray-700 hover:text-blue-600 hover:bg-blue-50",
                              "group flex items-center rounded py-1 px-1 text-sm leading-6 font-semibold w-full transition-all duration-200"
                            )}
                            aria-current={isActive ? "page" : undefined}
                          >
                            <item.icon
                              className={cn(
                                isActive
                                  ? "text-blue-600"
                                  : "text-gray-400 group-hover:text-blue-600",
                                "h-4 w-4 shrink-0 transition-colors duration-200"
                              )}
                              aria-hidden="true"
                            />
                            <span className="ml-1">{item.name}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-72 pt-16">
        <main className="py-10">
          <div className="px-4 sm:px-6 lg:px-8">
            <React.Suspense fallback={<LoadingScreen />}>
              <ErrorBoundary>
                <Outlet />
              </ErrorBoundary>
            </React.Suspense>
          </div>
        </main>
      </div>

      {/* Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "#333",
            color: "#fff",
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: "#22c55e",
              secondary: "#fff",
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: "#ef4444",
              secondary: "#fff",
            },
          },
        }}
      />
      <DevSidebar />
    </div>
  );
}
